import { useGetOrganizationSummary } from "@src/api/organization-summaries/get-organization-summary";
import { BillingActiveEntityViewModel } from "./types";
import { isLegacyPlan } from "@src/components/BillingPanel/components/BillingHeader/isLegacyPlan";
import { OrganizationSummarySubscriptionDto } from "@intelligems/ig-types/dtos/organization-summaries";

type BillingPlan = BillingActiveEntityViewModel;

const getLegacyPlanFromLegacySubscriptions = (
  legacySubscriptions: OrganizationSummarySubscriptionDto[]
): BillingPlan | undefined => {
  if (legacySubscriptions.length === 0) {
    return undefined;
  }

  // Seed the reduce with the first subscription so it never throws on a set
  // where no subscription carries a createdAtTs (all treated as 0).
  const [firstSubscription, ...remainingSubscriptions] = legacySubscriptions;
  const newestCancelledSubscription = remainingSubscriptions.reduce(
    (newest, subscription) =>
      (subscription.createdAtTs ?? 0) > (newest.createdAtTs ?? 0)
        ? subscription
        : newest,
    firstSubscription
  );

  const { interval, price: priceAmount } = newestCancelledSubscription;

  return {
    actions: [],
    analyticsTier: "n/a",
    chips: [],
    description: "",
    entityType: "plan",
    features: [],
    id: "legacy-plan",
    isMandatory: false,
    isSmartPricing: false,
    isUnlimited: false,
    minimumCommitmentMonths: 0,
    monthsActive: 0,
    name: "",
    price: {
      amount: priceAmount || 0,
      suffix: interval === "year" ? "/yr" : interval === "month" ? "/mo" : "",
    },
    // A legacy plan surfaced here is already over, so it takes the union's
    // over-and-done member rather than borrowing `scheduled_cancel`, which means a
    // cancellation is still *pending* on a live subscription. Cancelled-ness is
    // shown via `status` and the isLegacyPlan prop; the state keeps the object
    // truthful for anything that switches on it.
    state: "ended",
    status: {
      label: "Cancelled",
      tone: "error",
    },
  };
};

export const useBillingLegacyPlan = ({
  hasCurrentPlan,
}: Readonly<{ hasCurrentPlan: boolean }>) => {
  const { data: orgSummary } = useGetOrganizationSummary();

  const legacySubscriptions: OrganizationSummarySubscriptionDto[] =
    hasCurrentPlan
      ? []
      : orgSummary.org.organizationSubscriptions.filter((subscription) =>
          isLegacyPlan([subscription])
        );

  // Stripe emits the American "canceled"; other providers/records use the
  // British "cancelled". Both spellings are valid in the DTO, so match either.
  const isLegacyPlanCancelled = legacySubscriptions.some(
    (subscription) =>
      subscription.status === "canceled" || subscription.status === "cancelled"
  );

  const legacyPlan = getLegacyPlanFromLegacySubscriptions(legacySubscriptions);

  return { isLegacyPlanCancelled, legacyPlan, legacySubscriptions };
};
